import { useTranslation } from 'react-i18next';

import { Pagination } from '@/components/data/Pagination';

import { useMedicalStaffQuery } from '../hooks/use-medical-staff';
import { useMedicalStaffListStore } from '../store/medical-staff-list.store';
import {
  MedicalStaffListEmpty,
  MedicalStaffListSkeleton,
  MedicalStaffListTable,
} from './MedicalStaffListTable';

function MedicalStaffListError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('admin');
  return (
    <div
      role="alert"
      style={{
        padding: 16,
        border: '1px solid var(--destructive)',
        borderRadius: 8,
        background: '#FEE2E8',
        color: 'var(--destructive)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
      }}
    >
      <span>{t('medicalStaffManagement.list.loadError')}</span>
      <button type="button" className="btn btn-sm btn-outline" onClick={onRetry}>
        {t('medicalStaffManagement.actions.retry')}
      </button>
    </div>
  );
}

export function MedicalStaffListView() {
  const filters = useMedicalStaffListStore((s) => s.filters);
  const sort = useMedicalStaffListStore((s) => s.sort);
  const page = useMedicalStaffListStore((s) => s.page);
  const perPage = useMedicalStaffListStore((s) => s.perPage);
  const setPage = useMedicalStaffListStore((s) => s.setPage);

  // Empty strings are omitted from the wire so the mock/HTTP layer never
  // receives '' (which gets treated as "All").
  const params = {
    q: filters.search.trim() || undefined,
    department: filters.department || undefined,
    page,
    perPage,
    sortBy: sort.key,
    sortDir: sort.dir,
  };

  const { data, isLoading, error, refetch } = useMedicalStaffQuery(params);

  const isFiltered = Boolean(params.q) || Boolean(params.department);

  if (isLoading) return <MedicalStaffListSkeleton />;
  if (error) return <MedicalStaffListError onRetry={() => void refetch()} />;
  if (!data || data.items.length === 0)
    return <MedicalStaffListEmpty filtered={isFiltered} />;

  const totalPages = Math.max(1, Math.ceil(data.total / data.perPage));

  return (
    <>
      <MedicalStaffListTable staff={data.items} />
      <Pagination page={data.page} totalPages={totalPages} onPageChange={setPage} />
    </>
  );
}
